import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Collapse,
  Grid,
  IconButton,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Search as SearchIcon } from '@mui/icons-material';
import { parseFlightNumberInput } from '../utils/flightJourneyUtils';
import { formatFlightSchedule } from '../utils/flightScheduleUtils';
import { getFlightLegRouteLabel } from '../utils/flightRouteUtils';
import { normalizeAirlineField, resolveAirlineIata } from '../utils/airlineIata';
import {
  buildFlightLegLookupRequest,
  getFlightLegLookupKey,
  lookupFlightLeg,
  shouldAutoLookupFlightLeg,
} from '../utils/flightLegLookupUtils';

const EMPTY_LEG = {
  airline: '',
  flight_number: '',
  departure_airport: '',
  arrival_airport: '',
  departure_time: '',
  arrival_time: '',
};

const FlightLegsEditor = ({ legs, onChange, trip = null }) => {
  const { t } = useTranslation();
  const [lookupIndex, setLookupIndex] = useState(null);
  const [lookupMessages, setLookupMessages] = useState({});
  const [manualAirportLegs, setManualAirportLegs] = useState({});
  const autoLookupAttemptedRef = useRef(new Set());
  const pendingLookupIndexRef = useRef(null);
  const autoLookupTimerRef = useRef(null);
  const activeLookupRequestRef = useRef(0);
  const lookupAbortControllerRef = useRef(null);

  const updateLeg = useCallback((index, field, value) => {
    onChange((currentLegs) => currentLegs.map((leg, legIndex) => {
      if (legIndex !== index) {
        return leg;
      }

      const updatedLeg = { ...leg, [field]: value };

      if (field === 'flight_number') {
        const parsed = parseFlightNumberInput(value);
        if (parsed) {
          updatedLeg.airline = parsed.airline;
          updatedLeg.airline_code = parsed.airline;
          updatedLeg.flight_number = parsed.flight_number;
        } else {
          const iata = resolveAirlineIata(updatedLeg.airline, updatedLeg.airline_code);
          if (iata) {
            updatedLeg.airline = iata;
            updatedLeg.airline_code = iata;
          }
        }
      }

      if (field === 'airline') {
        // Keep free typing while focused; resolve known names to IATA on blur.
        updatedLeg.airline = String(value || '').toUpperCase();
      }

      // Changing flight identity invalidates prior airports (e.g. QR SFO→DOH → EK226).
      if (['flight_number', 'airline'].includes(field)) {
        updatedLeg.departure_airport = '';
        updatedLeg.arrival_airport = '';
      }

      return updatedLeg;
    }));

    if (['flight_number', 'airline', 'departure_time', 'departure_airport', 'arrival_airport'].includes(field)) {
      autoLookupAttemptedRef.current = new Set(
        [...autoLookupAttemptedRef.current].filter((key) => !key.startsWith(`${index}-`))
      );
      setLookupMessages((prev) => {
        if (!prev[index]) {
          return prev;
        }
        const next = { ...prev };
        delete next[index];
        return next;
      });
    }
  }, [onChange]);

  const resolveAirlineOnBlur = useCallback((index) => {
    onChange((currentLegs) => currentLegs.map((leg, legIndex) => {
      if (legIndex !== index) {
        return leg;
      }
      const normalized = normalizeAirlineField(leg.airline, leg.airline_code);
      if (!normalized || normalized === leg.airline) {
        return leg;
      }
      return {
        ...leg,
        airline: normalized,
        airline_code: resolveAirlineIata(normalized) || leg.airline_code || normalized,
      };
    }));
  }, [onChange]);

  // Legs loaded from storage may still have "Emirates" — normalize to EK.
  useEffect(() => {
    const needsNormalize = legs.some((leg) => {
      const normalized = normalizeAirlineField(leg.airline, leg.airline_code);
      return Boolean(normalized) && normalized !== String(leg.airline || '').trim();
    });
    if (!needsNormalize) {
      return;
    }
    onChange((currentLegs) => currentLegs.map((leg) => {
      const normalized = normalizeAirlineField(leg.airline, leg.airline_code);
      if (!normalized || normalized === leg.airline) {
        return leg;
      }
      return {
        ...leg,
        airline: normalized,
        airline_code: resolveAirlineIata(normalized) || normalized,
      };
    }));
  }, [legs, onChange]);

  const addLeg = () => {
    onChange((currentLegs) => [...currentLegs, { ...EMPTY_LEG }]);
  };

  const removeLeg = (index) => {
    onChange((currentLegs) => {
      if (currentLegs.length <= 1) {
        return currentLegs;
      }
      return currentLegs.filter((_, legIndex) => legIndex !== index);
    });
  };

  const lookupLeg = useCallback(async (index, currentLegs, { force = false } = {}) => {
    const leg = currentLegs[index];
    const lookupConfig = buildFlightLegLookupRequest(leg);
    if (!lookupConfig) {
      setLookupMessages((prev) => ({
        ...prev,
        [index]: t('itinerary.flightLegs.lookupNeedsDetails'),
      }));
      return null;
    }

    const lookupKey = getFlightLegLookupKey(index, leg);
    if (!lookupKey) {
      setLookupMessages((prev) => ({
        ...prev,
        [index]: t('itinerary.flightLegs.lookupNeedsDetails'),
      }));
      return null;
    }

    if (!force && autoLookupAttemptedRef.current.has(lookupKey)) {
      return null;
    }
    autoLookupAttemptedRef.current.add(lookupKey);

    lookupAbortControllerRef.current?.abort();
    lookupAbortControllerRef.current = new AbortController();
    const { signal } = lookupAbortControllerRef.current;

    const requestId = activeLookupRequestRef.current + 1;
    activeLookupRequestRef.current = requestId;

    setLookupIndex(index);
    setLookupMessages((prev) => ({
      ...prev,
      [index]: t('itinerary.flightLegs.lookingUp'),
    }));

    try {
      const { success, leg: result } = await lookupFlightLeg(leg, { signal });
      if (activeLookupRequestRef.current !== requestId) {
        return null;
      }

      if (!success) {
        setLookupMessages((prev) => ({
          ...prev,
          [index]: t('itinerary.flightLegs.lookupFailed'),
        }));
        return null;
      }

      onChange((prevLegs) => prevLegs.map((currentLeg, legIndex) => {
        if (legIndex === index) {
          return result;
        }

        if (
          legIndex === index + 1
          && result.arrival_airport
          && !currentLeg.departure_airport
        ) {
          return {
            ...currentLeg,
            departure_airport: result.arrival_airport,
          };
        }

        return currentLeg;
      }));

      setLookupMessages((prev) => ({
        ...prev,
        [index]: t('itinerary.flightLegs.routeLabel', {
          route: `${result.departure_airport} → ${result.arrival_airport}`,
        }),
      }));

      return result;
    } catch (error) {
      if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
        return null;
      }

      setLookupMessages((prev) => ({
        ...prev,
        [index]: t('itinerary.flightLegs.lookupFailed'),
      }));
      return null;
    } finally {
      setLookupIndex(null);
      pendingLookupIndexRef.current = null;
    }
  }, [onChange, t]);

  useEffect(() => {
    if (lookupIndex !== null || pendingLookupIndexRef.current !== null) {
      return undefined;
    }

    const nextIndex = legs.findIndex((leg, index) => (
      shouldAutoLookupFlightLeg(index, leg, autoLookupAttemptedRef.current)
    ));

    if (nextIndex === -1) {
      return undefined;
    }

    autoLookupTimerRef.current = window.setTimeout(() => {
      if (lookupIndex !== null || pendingLookupIndexRef.current !== null) {
        return;
      }

      pendingLookupIndexRef.current = nextIndex;
      lookupLeg(nextIndex, legs);
    }, 500);

    return () => {
      if (autoLookupTimerRef.current) {
        window.clearTimeout(autoLookupTimerRef.current);
        autoLookupTimerRef.current = null;
      }
    };
  }, [legs, lookupIndex, lookupLeg]);

  useEffect(() => () => {
    lookupAbortControllerRef.current?.abort();
  }, []);

  const toggleManualAirports = (index) => {
    setManualAirportLegs((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
        {t('itinerary.flightLegs.title')}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: 'text.secondary' }}>
        {t('itinerary.flightLegs.lookupHint')}
      </Typography>

      {legs.map((leg, index) => {
        const routeLabel = getFlightLegRouteLabel(leg, index, legs);
        const routeDisplay = routeLabel?.includes('→') ? routeLabel : null;

        return (
          <Box
            key={`flight-leg-${index}`}
            sx={{
              mb: 2,
              p: 2,
              borderRadius: 2,
              border: '1px solid #e2e8f0',
              backgroundColor: '#f8fafc',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#475569' }}>
                {t('itinerary.flightLegs.legNumber', { number: index + 1 })}
              </Typography>
              {legs.length > 1 && (
                <IconButton size="small" onClick={() => removeLeg(index)} aria-label={t('itinerary.flightLegs.removeLeg')}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('itinerary.flightLegs.flightNumber')}
                  placeholder="UA 1449"
                  value={leg.flight_number}
                  onChange={(e) => updateLeg(index, 'flight_number', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('itinerary.flightLegs.airline')}
                  value={leg.airline}
                  onChange={(e) => updateLeg(index, 'airline', e.target.value)}
                  onBlur={() => resolveAirlineOnBlur(index)}
                  helperText={t('itinerary.flightLegs.airlineHint')}
                  placeholder="EK"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label={t('itinerary.flightLegs.departure')}
                  value={leg.departure_time}
                  onChange={(e) => updateLeg(index, 'departure_time', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: trip?.start_date ? `${trip.start_date}T00:00` : undefined,
                    max: trip?.end_date ? `${trip.end_date}T23:59` : undefined,
                  }}
                />
                {formatFlightSchedule(leg.departure_time)?.full && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#475569', fontWeight: 600 }}>
                    {formatFlightSchedule(leg.departure_time).full}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label={t('itinerary.flightLegs.arrival')}
                  value={leg.arrival_time}
                  onChange={(e) => updateLeg(index, 'arrival_time', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: leg.departure_time || (trip?.start_date ? `${trip.start_date}T00:00` : undefined),
                    max: trip?.end_date ? `${trip.end_date}T23:59` : undefined,
                  }}
                />
                {formatFlightSchedule(leg.arrival_time)?.full && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#475569', fontWeight: 600 }}>
                    {formatFlightSchedule(leg.arrival_time).full}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                  {routeDisplay && (
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                      {t('itinerary.flightLegs.routeLabel', { route: routeDisplay })}
                    </Typography>
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={lookupIndex === index ? <CircularProgress size={14} /> : <SearchIcon />}
                    disabled={!leg.flight_number || !leg.departure_time || lookupIndex !== null}
                    onClick={() => lookupLeg(index, legs, { force: true })}
                  >
                    {t('itinerary.flightLegs.lookupRoute')}
                  </Button>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => toggleManualAirports(index)}
                    sx={{ textTransform: 'none' }}
                  >
                    {manualAirportLegs[index]
                      ? t('itinerary.flightLegs.hideAirports')
                      : t('itinerary.flightLegs.editAirports')}
                  </Button>
                </Box>
                {lookupMessages[index] && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mt: 0.75,
                      color: routeDisplay
                        ? 'success.main'
                        : lookupMessages[index] === t('itinerary.flightLegs.lookupFailed')
                          ? 'warning.main'
                          : 'text.secondary',
                    }}
                  >
                    {lookupMessages[index]}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                <Collapse in={Boolean(manualAirportLegs[index])}>
                  <Grid container spacing={2} sx={{ pt: 0.5 }}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label={t('itinerary.flightLegs.from')}
                        placeholder="SFO"
                        value={leg.departure_airport}
                        onChange={(e) => updateLeg(index, 'departure_airport', e.target.value.toUpperCase())}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label={t('itinerary.flightLegs.to')}
                        placeholder="EWR"
                        value={leg.arrival_airport}
                        onChange={(e) => updateLeg(index, 'arrival_airport', e.target.value.toUpperCase())}
                      />
                    </Grid>
                  </Grid>
                </Collapse>
              </Grid>
            </Grid>
          </Box>
        );
      })}

      <Button variant="outlined" startIcon={<AddIcon />} onClick={addLeg} sx={{ mt: 1 }}>
        {t('itinerary.flightLegs.addLeg')}
      </Button>
    </Box>
  );
};

export default FlightLegsEditor;
